import React from "react";
import formatDate from "../../../../utils/formatDate";

// Material Imports
import Button from "@mui/material/Button";

// Import CSS
import "./index.css";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { DatePicker } from "@mui/x-date-pickers";

import moment from "moment";
import Loading from "components/common/loading/Loading";
import dayjs from "dayjs";
import { useDownloadBillingHistoryMutation } from "app/store/patient/api/patientApi";
import { downloadFile } from "app/utils/file";
import { showErrorToast, showSuccessToast } from "utils/toast";
import ModalWrapper from "components/common/modals/modalWrapper/ModalWrapper";
import { Formik } from "formik";
import { billingHistoryValidationSchema } from "schemas/billingHistoryValidationSchema";
import SubmitButton from "components/Button/SubmitButton";
import { FormHelperText } from "@mui/material";
import { getFormattedDate } from "utils/date";
import { DATE_FORMAT } from "static/date";
import { filterNonUCHAccounts } from "utils/account.util";

export default function UserModal(props) {
  const end = moment();
  const initialStartDate = moment().subtract(1, "month").toDate();
  const initialEndDate = end.toDate();

  const { accounts, billingModalOpen, closeBillingModal } = props;
  const filteredAccounts = filterNonUCHAccounts(accounts);

  const onClose = () => {
    closeBillingModal();
  };

  const [downloadBillingHistory, { isLoading }] =
    useDownloadBillingHistoryMutation();

  const getBillingHistory = async (values) => {
    try {
      const { startDate, endDate, account } = values;
      const formattedStartDate = formatDate(startDate, "MM-DD-YYYY");
      const formattedEndDate = formatDate(endDate, "MM-DD-YYYY");

      const endDatePlus = moment(endDate).add(1, "day");
      const formattedEndDatePlus = formatDate(endDatePlus, "MM-DD-YYYY");

      const payload = {
        startDate: formattedStartDate,
        endDate: formattedEndDatePlus,
        account,
      };

      const response = await downloadBillingHistory(payload);
      if (response.error) {
        throw response.error;
      }

      const fileName = `${account} - billing history - ${formattedStartDate} - ${formattedEndDate}.xlsx`;
      downloadFile(response.data, fileName);
      showSuccessToast("Billing History downloaded successfully.");
      onClose();
    } catch (error) {
      showErrorToast(error.message);
    }
  };

  return (
    <ModalWrapper
      aria-labelledby="simple-modal-title"
      aria-describedby="simple-modal-description"
      show={billingModalOpen}
      onClose={onClose}
      title="Billing History"
    >
      {isLoading ? (
        <Loading height="100px" />
      ) : (
        <Formik
          initialValues={{
            account:
              filteredAccounts?.length === 1 ? filteredAccounts?.[0]?.name : "",
            startDate: getFormattedDate(initialStartDate, DATE_FORMAT.DEFAULT),
            endDate: getFormattedDate(initialEndDate, DATE_FORMAT.DEFAULT),
          }}
          validationSchema={billingHistoryValidationSchema}
          onSubmit={getBillingHistory}
          enableReinitialize
        >
          {({
            values,
            setFieldValue,
            handleChange,
            handleSubmit,
            errors,
            touched,
          }) => {
            return (
              <>
                <div style={{ marginBottom: "20px", marginTop: "20px" }}>
                  <FormControl
                    variant="outlined"
                    fullWidth
                    error={touched.account && Boolean(errors.account)}
                  >
                    <InputLabel id="issue-label">Select Account</InputLabel>
                    <Select
                      id="accounts"
                      value={
                        filteredAccounts?.length > 1
                          ? values.account
                          : filteredAccounts?.[0]?.name
                      }
                      onChange={handleChange("account")}
                      disabled={filteredAccounts?.length === 1}
                      label="Select Account"
                    >
                      {filteredAccounts?.map(({ name }, index) => (
                        <MenuItem key={index} value={name}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                    {touched.account && Boolean(errors.account) && (
                      <FormHelperText error>{errors.account}</FormHelperText>
                    )}
                  </FormControl>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "10px",
                  }}
                >
                  <FormControl
                    fullWidth
                    error={touched.startDate && Boolean(errors.startDate)}
                  >
                    <DatePicker
                      margin="none"
                      id="billing_history_start"
                      label="Start Date"
                      className="view-pdf__input"
                      format={DATE_FORMAT.DEFAULT}
                      name="startDate"
                      value={values.startDate ? dayjs(values.startDate) : null}
                      onChange={(date) => {
                        const value = date
                          ? getFormattedDate(date, DATE_FORMAT.DEFAULT)
                          : null;
                        setFieldValue("startDate", value);
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          error: touched.startDate && Boolean(errors.startDate),
                          helperText: touched.startDate && errors.startDate,
                        },
                      }}
                    />
                  </FormControl>
                  <FormControl fullWidth>
                    <DatePicker
                      margin="none"
                      id="billing_history_end"
                      label="End Date"
                      className="view-pdf__input"
                      format={DATE_FORMAT.DEFAULT}
                      value={values.endDate ? dayjs(values.endDate) : null}
                      onChange={(date) => {
                        const value = date
                          ? getFormattedDate(date, DATE_FORMAT.DEFAULT)
                          : null;
                        setFieldValue("endDate", value);
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: touched.endDate && Boolean(errors.endDate),
                          helperText: touched.endDate && errors.endDate,
                        },
                      }}
                    />
                  </FormControl>
                </div>
                <div className="view-pdf__button-container">
                  <Button
                    onClick={onClose}
                    className="view-pdf__button"
                    variant="outlined"
                    color="secondary"
                  >
                    Cancel
                  </Button>
                  <SubmitButton
                    buttonText="Download"
                    className="view-pdf__button"
                    handleClick={handleSubmit}
                  />
                </div>
              </>
            );
          }}
        </Formik>
      )}
    </ModalWrapper>
  );
}
